import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, ArrowDownUp } from "lucide-react";
import { t } from "@/i18n/sk";
import { getExpansions, getEras, resolveImg } from "@/lib/api";
import { HoloBackground } from "@/components/HoloBackground";

function Badge({ text }) {
  if (!text) return null;
  return <span className="inline-block rounded-full border border-violet-glow/40 bg-violet-glow/15 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-violet-200">{text}</span>;
}

function SeriesCard({ exp, i }) {
  return (
    <Link
      to={`/serie/${exp.slug}`}
      data-testid={`expansion-tile-${exp.slug}`}
      className="group relative flex min-h-[18rem] flex-col justify-end overflow-hidden rounded-2xl border border-white/8 bg-vault-card p-6 shadow-sm transition-all duration-300 hover:border-violet-glow/50 hover:shadow-[0_16px_50px_rgba(139,92,246,0.18)] animate-fade-up"
      style={{ animationDelay: `${i * 60}ms` }}
    >
      <div className="absolute inset-0">
        <img src={resolveImg(exp.image_url)} alt={exp.name} loading="lazy" className="h-full w-full object-cover opacity-20 transition-all duration-500 group-hover:scale-105 group-hover:opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-vault-card via-vault-card/90 to-vault-card/50" />
      </div>
      <div className="relative z-10">
        <div className="mb-2 flex items-center gap-2">
          <span className="overline">{exp.release_label || exp.era_name}</span>
          <Badge text={exp.badge} />
        </div>
        <h3 className="font-display text-xl font-bold text-white">{exp.name}</h3>
        <p className="mt-2 line-clamp-2 text-sm text-slate-300">{exp.description}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-300 group-hover:text-cyan-bright">Zobraziť produkty <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
      </div>
    </Link>
  );
}

function sortSeries(list, dir) {
  const dated = list.filter((e) => e.release_date);
  const undated = list.filter((e) => !e.release_date);
  dated.sort((a, b) => dir === "newest" ? b.release_date.localeCompare(a.release_date) : a.release_date.localeCompare(b.release_date));
  return [...dated, ...undated];
}

export default function SeriesPage() {
  const [expansions, setExpansions] = useState([]);
  const [eras, setEras] = useState([]);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    getExpansions().then(setExpansions).catch(() => {});
    getEras().then(setEras).catch(() => {});
  }, []);

  const noEra = useMemo(() => sortSeries(expansions.filter((e) => !e.era_slug), sort), [expansions, sort]);
  const selectClass = "rounded-lg border border-white/10 bg-vault-card px-3 py-2 text-sm text-white shadow-sm focus:border-cyan-glow/50 focus:outline-none";

  return (
    <div className="vault-bg">
      <section className="relative overflow-hidden border-b border-white/8">
        <HoloBackground />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="overline mb-3 block">Katalóg</span>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{t.nav.series}</h1>
          <p className="mt-4 max-w-2xl text-slate-300">Prehľadávaj série podľa éry. Každá éra zahŕňa všetky série, ktoré pod ňu patria.</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-300"><ArrowDownUp className="h-4 w-4 text-cyan-300" /> Zoradiť série</span>
          <select data-testid="series-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={selectClass}>
            <option value="newest">Najnovšie vydania</option>
            <option value="oldest">Najstaršie vydania</option>
          </select>
        </div>

        {eras.map((era) => {
          const items = sortSeries(expansions.filter((e) => e.era_slug === era.slug), sort);
          if (items.length === 0) return null;
          return (
            <section key={era.slug} className="mb-16" data-testid={`era-section-${era.slug}`}>
              <div className="relative mb-7 overflow-hidden rounded-2xl border border-white/8 bg-vault-card p-7 shadow-sm">
                <div className="absolute inset-0">
                  <img src={resolveImg(era.image_url)} alt="" className="h-full w-full object-cover opacity-15" />
                  <div className="absolute inset-0 bg-gradient-to-r from-vault-card via-vault-card/90 to-vault-card/60" />
                </div>
                <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <span className="overline mb-2 flex items-center gap-2"><Layers className="h-3.5 w-3.5" /> Éra</span>
                    <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{era.name}</h2>
                    <p className="mt-2 max-w-2xl text-sm text-slate-300">{era.description}</p>
                  </div>
                  <Link to={`/era/${era.slug}`} data-testid={`era-view-${era.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-cyan-glow/50">
                    Celá éra <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {items.map((exp, i) => <SeriesCard key={exp.id} exp={exp} i={i} />)}
              </div>
            </section>
          );
        })}

        {noEra.length > 0 && (
          <section className="mb-16">
            <h2 className="mb-7 font-display text-2xl font-bold text-white">Ostatné série</h2>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {noEra.map((exp, i) => <SeriesCard key={exp.id} exp={exp} i={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
